// The systems inventory, in SerenityConsult's look: which kinds of system each department runs on (amber marks work
// kept outside a system of record: spreadsheets, email or chat, paper), then every system with what it costs, who
// owns it, the processes that use it and the plan for it. A system is retired at Re-catalog, with the request that
// made it unnecessary, or by hand.
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { useLoad } from "../hooks";
import { SUITE } from "../suite";
import { imp, day, money, type SystemDetail, type SystemFields, type SystemRow } from "./impApi";
import { Bar, Head, Kpis, Seg, Win, useSort } from "./SerKit";
import "./ser.css";

const KINDS: Record<string, string> = { erp: "ERP", in_house: "Built in-house", cloud_app: "Cloud app", point_solution: "Point solution", spreadsheet: "Spreadsheet", email_chat: "Email or chat", paper: "Paper", other: "Other" };
const PLANS: Record<string, string> = { review: "Not decided", keep: "Keep", replace: "Replace with CourageMFG", retire: "Retire" };
/** Kinds that keep work outside a system of record. */
const OUTSIDE = new Set(["spreadsheet", "email_chat", "paper"]);
const areaName = (id: string | null) => (id ? SUITE.find((d) => d.id === id)?.name ?? id : "No department");
type View = "in_use" | "retired" | "all";

function Plan({ plan, label, retiredAt }: { plan: string; label: string; retiredAt: string | null }) {
  if (retiredAt) return <span className="pill g">Retired {day(retiredAt)}</span>;
  return <span className={`pill ${plan === "replace" || plan === "retire" ? "a" : plan === "keep" ? "m" : ""}`}>{label}</span>;
}

export function SystemsPage() {
  const [sp, setSp] = useSearchParams();
  const view = (sp.get("view") ?? "in_use") as View;
  const [tick, setTick] = useState(0);
  const all = useLoad(() => imp.systems("all"), [tick]);
  const [adding, setAdding] = useState(false);
  const [cell, setCell] = useState<string | null>(null);
  const d = all.data, t = d?.totals;
  const inUse = useMemo(() => (d?.systems ?? []).filter((s) => !s.retiredAt), [d]);
  const list = useMemo(() => (d?.systems ?? []).filter((s) => (view === "all" ? true : view === "retired" ? !!s.retiredAt : !s.retiredAt)), [d, view]);
  // Departments by kind of system: a department runs on a system when a process of its passes through it.
  const matrix = useMemo(() => {
    const m = new Map<string, Map<string, SystemRow[]>>();
    for (const s of inUse) {
      const areas = s.processes.length ? [...new Set(s.processes.map((p) => p.area ?? ""))] : ["-"];
      for (const a of areas) {
        if (!m.has(a)) m.set(a, new Map());
        const row = m.get(a)!;
        row.set(s.kind, [...(row.get(s.kind) ?? []), s]);
      }
    }
    const kinds = Object.keys(KINDS).filter((k) => inUse.some((s) => s.kind === k));
    const rows = [...m.keys()].sort((a, b) => (a === "-" ? 1 : b === "-" ? -1 : areaName(a || null).localeCompare(areaName(b || null))));
    return { m, kinds, rows };
  }, [inUse]);
  const outside = inUse.filter((s) => OUTSIDE.has(s.kind));
  const outsideProcs = new Set(outside.flatMap((s) => s.processes.map((p) => p.number))).size;
  const { sorted, th } = useSort<SystemRow>(list, {
    name: (s) => s.name.toLowerCase(), cost: (s) => s.costPerYear, owner: (s) => s.owner?.toLowerCase() ?? null, users: (s) => s.users, procs: (s) => s.processes.length,
  }, { key: "cost", dir: "desc" });
  const max = Math.max(1, ...list.map((s) => s.costPerYear ?? 0));
  const picked = cell ? (() => { const [a, k] = cell.split("|"); return { a, k, systems: matrix.m.get(a)?.get(k) ?? [] }; })() : null;
  const rowName = (a: string) => (a === "-" ? "Not in a process yet" : areaName(a || null));
  return (
    <>
      <CommandBar status={t ? `${t.inUse} in use · ${t.undecided} not decided · ${t.retired} retired` : "Loading…"}>
        <button className="cmd primary" onClick={() => setAdding((x) => !x)} aria-pressed={adding}>Add a system</button>
        <Link className="cmd" to="/improve/catalog">Process catalog</Link>
      </CommandBar>
      <div className="page ser">
        <Head kicker="Improvement · Systems inventory" title="What each department runs on"
          lede="Every system the company runs on, what it costs, the processes that pass through it and the plan for it. Work kept in spreadsheets, email and paper is where improvement starts." />
        <ErrorBox error={all.error} />
        {adding && <NewSystem onDone={() => { setAdding(false); setTick((x) => x + 1); }} />}
        <Kpis items={[
          { label: "Systems in use", value: t ? t.inUse : "–", sub: t ? `${money(t.costPerYear)} a year` : "" },
          { label: "Outside a system of record", value: d ? outside.length : "–", sub: d ? `${outsideProcs} process${outsideProcs === 1 ? "" : "es"} pass through them` : "", tone: outside.length ? "dn" : undefined },
          { label: "To replace or retire", value: t ? money(t.toChangeCost) : "–", sub: t ? `${t.toChange} system${t.toChange === 1 ? "" : "s"} · ${t.undecided} not decided` : "" },
          { label: "Retired", value: t ? t.retired : "–", sub: t ? `${money(t.savedPerYear)} a year saved` : "", tone: t?.retired ? "up" : undefined },
        ]} />
        <div className="s-grid2 wide-r">
          <Win title="Departments by kind of system" sub="tap a dot">
            {!d ? <div className="empty">Loading…</div> : !inUse.length ? <div className="empty">No system in use yet. <button className="mini" onClick={() => setAdding(true)}>Add the first one</button></div> : (<>
              <div className="scroll-x" tabIndex={0} role="region" aria-label="Departments by kind of system">
                <table className="inv">
                  <thead><tr><th>Department</th>{matrix.kinds.map((k) => <th key={k} scope="col">{KINDS[k]}</th>)}</tr></thead>
                  <tbody>{matrix.rows.map((a) => (
                    <tr key={a}><td>{rowName(a)}</td>
                      {matrix.kinds.map((k) => {
                        const ss = matrix.m.get(a)?.get(k) ?? [];
                        const key = `${a}|${k}`;
                        return <td key={k}>{ss.length > 0 && (
                          <button className={`d ${OUTSIDE.has(k) ? "s" : ""}`} aria-pressed={cell === key} onClick={() => setCell(cell === key ? null : key)}
                            aria-label={`${rowName(a)}: ${ss.length} ${KINDS[k].toLowerCase()}${ss.length === 1 ? "" : " systems"}`} title={ss.map((s) => s.name).join(", ")}><i /></button>)}</td>;
                      })}
                    </tr>))}
                  </tbody>
                </table>
              </div>
              {picked && picked.systems.length > 0 && (
                <div className="picked" aria-live="polite">
                  <b>{rowName(picked.a)} · {KINDS[picked.k]}</b>
                  {picked.systems.map((s) => (
                    <div key={s.id}><Link to={`/improve/systems/${s.id}`}>{s.name}</Link>
                      <span className="muted"> · {s.processes.filter((p) => picked.a === "-" || (p.area ?? "") === picked.a).map((p) => p.name).join(", ") || "no process yet"}</span></div>))}
                </div>)}
              <p className="note"><i className="amb" />Amber: work kept outside a system of record (spreadsheets, email or chat, paper).</p>
            </>)}
          </Win>
          <Win title="Systems" sub="tap a column to sort" actions={<Seg<View> label="Show" value={view} onChange={(v) => setSp(v === "in_use" ? {} : { view: v })} options={[["in_use", "In use"], ["retired", "Retired"], ["all", "All"]]} />}>
            {!d ? <div className="empty">Loading…</div> : !list.length ? (
              <div className="empty">{view === "retired" ? "No system is retired yet." : <>No systems yet. <button className="mini" onClick={() => setAdding(true)}>Add the first one</button></>}</div>
            ) : (
              <div className="scroll-x" tabIndex={0} role="region" aria-label="Systems">
                <table className="tbl">
                  <thead><tr>{th("name", "System", { text: true })}{th("cost", "Cost a year")}{th("owner", "Owner", { text: true, className: "hide-m" })}{th("procs", "Processes", { r: true, className: "hide-m" })}<th className="hide-m">Plan</th></tr></thead>
                  <tbody>{sorted.map((s) => (
                    <tr key={s.id}>
                      <td><strong><Link to={`/improve/systems/${s.id}`}>{s.name}</Link></strong><span className="sub">{[s.kindLabel, s.vendor, s.plan === "replace" && s.replacedBy ? `→ ${s.replacedBy}` : null].filter(Boolean).join(" · ")}</span><span className="show-m" style={{ marginTop: 4 }}><Plan plan={s.plan} label={s.planLabel} retiredAt={s.retiredAt} /></span></td>
                      <td><Bar value={s.costPerYear} max={max} width={56} amber={OUTSIDE.has(s.kind)} /></td>
                      <td className="hide-m">{s.owner ?? <span className="muted">–</span>}</td>
                      <td className="r mono hide-m" title={s.processes.map((p) => `${p.number} ${p.name}`).join("\n")}>{s.processes.length}</td>
                      <td className="hide-m"><Plan plan={s.plan} label={s.planLabel} retiredAt={s.retiredAt} /></td>
                    </tr>))}
                  </tbody>
                </table>
              </div>)}
          </Win>
        </div>
      </div>
    </>
  );
}

function NewSystem({ onDone }: { onDone: () => void }) {
  const nav = useNavigate();
  const [b, setB] = useState({ name: "", kind: "point_solution", costPerYear: "", plan: "review" });
  const [err, setErr] = useState<unknown>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    try { const s = await imp.createSystem({ name: b.name, kind: b.kind, plan: b.plan, costPerYear: b.costPerYear.trim() === "" ? null : Number(b.costPerYear) }); onDone(); nav(`/improve/systems/${s.id}`); } catch (x) { setErr(x); }
  };
  const set = (k: keyof typeof b) => (e: { target: { value: string } }) => setB({ ...b, [k]: e.target.value });
  return (
    <div style={{ marginBottom: 16 }}>
      <Win title="Add a system">
        <form className="win-body" onSubmit={save}>
          <div className="form-grid">
            <div className="field"><label htmlFor="ns-n">System</label><input id="ns-n" className="input" required placeholder="e.g. NCR spreadsheet" value={b.name} onChange={set("name")} /></div>
            <div className="field"><label htmlFor="ns-k">Kind</label><select id="ns-k" className="select" value={b.kind} onChange={set("kind")}>{Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div className="field"><label htmlFor="ns-c">Cost a year (USD)</label><input id="ns-c" className="input" type="number" min={0} step="any" value={b.costPerYear} onChange={set("costPerYear")} /><span className="hint">Licenses, hosting and support</span></div>
            <div className="field"><label htmlFor="ns-p">Plan</label><select id="ns-p" className="select" value={b.plan} onChange={set("plan")}>{Object.entries(PLANS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          </div>
          <ErrorBox error={err} />
          <div className="btn-row"><button className="s-btn p">Add to the inventory</button><button type="button" className="s-btn" onClick={onDone}>Cancel</button></div>
        </form>
      </Win>
    </div>
  );
}

export function SystemPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [tick, setTick] = useState(0);
  const r = useLoad(() => imp.system(id), [id, tick]);
  const [err, setErr] = useState<unknown>(null);
  const [edit, setEdit] = useState(false);
  const act = async (fn: () => Promise<unknown>) => { setErr(null); try { await fn(); setTick((t) => t + 1); return true; } catch (x) { setErr(x); return false; } };
  const s = r.data;
  if (r.error) return <div className="page ser"><ErrorBox error={r.error} /><Link to="/improve/systems">Back to systems</Link></div>;
  if (!s) return <div className="page ser"><div className="empty">Loading…</div></div>;
  return (
    <>
      <CommandBar status={<>{s.retiredAt ? `Retired ${day(s.retiredAt)}` : s.planLabel}</>}>
        {!s.retiredAt && <button className="cmd primary" onClick={() => { const note = prompt(`Retire ${s.name}? Say why (optional).`); if (note !== null) act(() => imp.retireSystem(s.id, note.trim() || undefined)); }}>Retire</button>}
        {s.retiredAt && <button className="cmd" onClick={() => act(() => imp.reinstateSystem(s.id))}>Back in use</button>}
        <Link className="cmd" to="/improve/systems">All systems</Link>
        <button className="cmd" onClick={() => { if (confirm(`Delete ${s.name} from the inventory? Processes that use it lose the link.`)) act(async () => { await imp.deleteSystem(s.id); nav("/improve/systems"); }); }}>Delete</button>
      </CommandBar>
      <div className="page ser">
        <Head kicker={<>Systems inventory · {s.kindLabel}</>} title={s.name} lede={[s.vendor, s.owner ? `Owner: ${s.owner}` : null].filter(Boolean).join(" · ") || undefined} />
        <ErrorBox error={err} />
        {s.retiredAt && <div className="msg info">Retired {day(s.retiredAt)}{s.retiredWith ? <> with <Link to={`/factory/r/${s.retiredWith.number}`}>{s.retiredWith.number} {s.retiredWith.title}</Link></> : ""}{s.retiredNote && !s.retiredWith ? `: ${s.retiredNote}` : ""}. Its cost a year counts as saved.</div>}
        {OUTSIDE.has(s.kind) && !s.retiredAt && <div className="msg info">This is work kept outside a system of record. Each process that passes through it is a place to start improving.</div>}
        <Kpis items={[
          { label: "Cost a year", value: money(s.costPerYear), sub: "Licenses, hosting and support" },
          { label: "Users", value: s.users ?? "–" },
          { label: "Processes", value: s.processes.length, sub: `${new Set(s.processes.map((p) => p.area)).size} department${new Set(s.processes.map((p) => p.area)).size === 1 ? "" : "s"}` },
        ]} />
        <div className="s-grid2 wide-l">
          <div className="s-stack">
            <Win title="Processes that pass through it">
              {!s.processes.length ? <p className="empty">No process in the catalog uses it yet. Add it to a process from the <Link to="/improve/catalog">process catalog</Link>.</p> : (
                <div className="scroll-x"><table className="tbl">
                  <thead><tr><th>Process</th><th>Department</th></tr></thead>
                  <tbody>{s.processes.map((p) => <tr key={p.number}><td><strong><Link to={`/improve/catalog/${p.number}`}>{p.name}</Link></strong><span className="sub mono">{p.number}</span></td><td>{areaName(p.area)}</td></tr>)}</tbody>
                </table></div>)}
            </Win>
            {s.notes && !edit && <Win title="Notes"><p className="win-body" style={{ margin: 0, whiteSpace: "pre-wrap" }}>{s.notes}</p></Win>}
          </div>
          <div className="s-stack">
            {edit ? <EditSystem s={s} onDone={(ok) => { setEdit(false); if (ok) setTick((t) => t + 1); }} /> : (
              <Win title="Details" actions={<button className="mini" onClick={() => setEdit(true)}>Edit</button>}>
                <dl className="s-dl win-body" style={{ margin: 0 }}>
                  <dt>Kind</dt><dd>{s.kindLabel}</dd>
                  <dt>Vendor</dt><dd>{s.vendor ?? "–"}</dd>
                  <dt>Owner</dt><dd>{s.owner ?? "–"}</dd>
                  <dt>Users</dt><dd>{s.users ?? "–"}</dd>
                  <dt>Cost a year</dt><dd className="mono">{money(s.costPerYear)}</dd>
                  <dt>Plan</dt><dd><Plan plan={s.plan} label={s.planLabel} retiredAt={null} />{s.plan === "replace" && s.replacedBy && <span className="muted"> with {s.replacedBy}</span>}</dd>
                </dl>
              </Win>)}
          </div>
        </div>
      </div>
    </>
  );
}

function EditSystem({ s, onDone }: { s: SystemDetail; onDone: (saved: boolean) => void }) {
  const [b, setB] = useState({ name: s.name, kind: s.kind, vendor: s.vendor ?? "", owner: s.owner ?? "", users: s.users?.toString() ?? "", costPerYear: s.costPerYear?.toString() ?? "", plan: s.plan, replacedBy: s.replacedBy ?? "", notes: s.notes });
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => setErr(null), [b]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const body: SystemFields = { ...b, users: b.users.trim() === "" ? null : Number(b.users), costPerYear: b.costPerYear.trim() === "" ? null : Number(b.costPerYear), vendor: b.vendor || null, owner: b.owner || null, replacedBy: b.replacedBy || null };
    try { await imp.updateSystem(s.id, body); onDone(true); } catch (x) { setErr(x); }
  };
  const set = (k: keyof typeof b) => (e: { target: { value: string } }) => setB({ ...b, [k]: e.target.value });
  return (
    <Win title="Details">
      <form className="win-body" onSubmit={save}>
        <div className="field"><label htmlFor="es-n">System</label><input id="es-n" className="input" required value={b.name} onChange={set("name")} /></div>
        <div className="field"><label htmlFor="es-k">Kind</label><select id="es-k" className="select" value={b.kind} onChange={set("kind")}>{Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div className="field"><label htmlFor="es-c">Cost a year (USD)</label><input id="es-c" className="input" type="number" min={0} step="any" value={b.costPerYear} onChange={set("costPerYear")} /></div>
        <div className="field"><label htmlFor="es-p">Plan</label><select id="es-p" className="select" value={b.plan} onChange={set("plan")}>{Object.entries(PLANS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <details className="s-more" open={!!(s.vendor || s.owner || s.users || s.replacedBy || s.notes) || undefined}>
          <summary>More details</summary>
          <div className="field"><label htmlFor="es-v">Vendor</label><input id="es-v" className="input" value={b.vendor} onChange={set("vendor")} /></div>
          <div className="field"><label htmlFor="es-o">Owner</label><input id="es-o" className="input" value={b.owner} onChange={set("owner")} /></div>
          <div className="field"><label htmlFor="es-u">Users</label><input id="es-u" className="input" type="number" min={0} step={1} value={b.users} onChange={set("users")} /></div>
          {b.plan === "replace" && <div className="field"><label htmlFor="es-r">Replaced by</label><input id="es-r" className="input" placeholder="e.g. CourageMFG Quality" value={b.replacedBy} onChange={set("replacedBy")} /></div>}
          <div className="field"><label htmlFor="es-nt">Notes</label><textarea id="es-nt" className="textarea" rows={3} value={b.notes} onChange={set("notes")} /></div>
        </details>
        <ErrorBox error={err} />
        <div className="btn-row"><button className="s-btn p">Save</button><button type="button" className="s-btn" onClick={() => onDone(false)}>Cancel</button></div>
      </form>
    </Win>
  );
}
